import {
  Button,
  Section,
  SectionContent,
  spacing,
  useBooleanState,
} from '@retailer-platform/shared-components'
import React, { Fragment, type FunctionComponent, useCallback, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { type FormContextValues } from 'react-hook-form'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { type ChildCollection } from 'domains/collections/types/collections.types'
import { type CollectionTypeEnum } from 'domains/collections/api'
import { CollectionsSelectCollectionModal } from 'domains/collections/components/collections-form/components/select-collection-modal/CollectionsSelectCollectionModal'
import { CollectionsChildCollectionsList } from './CollectionsChildCollectionsList'

interface CollectionsChildCollectionsProps {
  register: FormContextValues<CollectionsFormData>['register']
  errors: FormContextValues<CollectionsFormData>['errors']
  setValue: FormContextValues<CollectionsFormData>['setValue']
  getValues: FormContextValues<CollectionsFormData>['getValues']
  collectionId: string
  retailerId?: string
  collectionType: CollectionTypeEnum
}

export const CollectionsChildCollections: FunctionComponent<CollectionsChildCollectionsProps> = ({
  register,
  getValues,
  setValue,
  collectionId,
  retailerId,
  collectionType,
}) => {
  const messages = useDomainMessages({
    title: 'collectionsDomain.collectionsForm.section.childCollections.title',
    addButton: 'collectionsDomain.collectionsForm.section.childCollections.addButton',
    modalTitle: 'collectionsDomain.collectionsForm.section.selectCollectionModal.selectChild',
  })

  const [isModalOpen, showModal, hideModal] = useBooleanState(false)

  const [childCollections, setChildCollections] = useState<ChildCollection[]>(
    getValues('children') ?? []
  )
  const addChildCollection = useCallback(
    (collection: ChildCollection) => {
      hideModal()
      if (!childCollections.includes(collection)) {
        setChildCollections(prevCollections => [...prevCollections, collection])
      }
    },
    [childCollections, setChildCollections, hideModal]
  )
  const deleteChildCollection = useCallback(
    (collectionIdToDelete: string) => {
      setChildCollections(prevCollections =>
        prevCollections.filter(({ id }) => id !== collectionIdToDelete)
      )
    },
    [setChildCollections]
  )

  useEffect(() => {
    register({ name: 'childrenIds' })
  }, [register])

  useEffect(() => {
    // update childrenIds whenever childCollections change
    setValue(
      'childrenIds',
      childCollections.map(({ id }) => id)
    )
  }, [childCollections, setValue])

  return (
    <Fragment>
      <Section
        title={messages.title}
        headerElement={
          <Button variant="secondary" onClick={showModal}>
            {messages.addButton}
          </Button>
        }
        wrapperStyles={css({
          marginTop: spacing.X32,
        })}
      >
        <SectionContent>
          <CollectionsChildCollectionsList
            collections={childCollections}
            onDelete={deleteChildCollection}
          />
        </SectionContent>
      </Section>

      {isModalOpen && (
        <CollectionsSelectCollectionModal
          title={messages.modalTitle}
          onSelect={addChildCollection}
          onClose={hideModal}
          retailerId={retailerId}
          collectionId={collectionId}
          collectionType={collectionType}
        />
      )}
    </Fragment>
  )
}
